import { useState, useRef, useMemo } from "react";
import { useDrugs, type Drug } from "@/lib/queries";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useCreateDrug, useUpdateDrug, useDeleteDrug, useBulkDeleteDrugs, useBulkImportDrugs } from "@/lib/mutations";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Search, Pencil, Trash, Upload, Download } from "lucide-react";
import { FORMULATIONS } from "@emr/shared/constants";
import { apiFetchBlob } from "@/lib/api";
import { toast } from "sonner";

function DrugFormDialog({ open, onOpenChange, drug }: { open: boolean; onOpenChange: (v: boolean) => void; drug?: Drug | null }) {
  const [form, setForm] = useState({
    name: drug?.name ?? "", composition: drug?.composition ?? "", formulation: drug?.formulation ?? "Tablet",
    strength: drug?.strength ?? "",
  });
  const create = useCreateDrug();
  const update = useUpdateDrug();

  const submit = async () => {
    try {
      if (drug) await update.mutateAsync({ id: drug.id, body: form });
      else await create.mutateAsync(form);
      onOpenChange(false);
    } catch { /* handled */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{drug ? "Edit drug" : "Add drug"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Composition</Label><Input value={form.composition} onChange={(e) => setForm({ ...form, composition: e.target.value })} /></div>
          <div><Label>Formulation</Label>
            <Select onValueChange={(v) => setForm({ ...form, formulation: v })} defaultValue={form.formulation ?? "Tablet"}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{FORMULATIONS.map((f: any) => <SelectItem key={f.value ?? f} value={f.value ?? f}>{f.label ?? f}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Strength</Label><Input value={form.strength} onChange={(e) => setForm({ ...form, strength: e.target.value })} placeholder="e.g. 500mg" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!form.name || create.isPending || update.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function DrugsPage() {
  const { isAdmin, permissions } = useAuth();
  const canCreate = isAdmin || permissions.has("drugs.create");
  const canEdit = isAdmin || permissions.has("drugs.edit");
  const canDelete = isAdmin || permissions.has("drugs.delete");
  const canImport = isAdmin || permissions.has("drugs.bulkimport");
  const [q, setQ] = useState("");
  const [editDrug, setEditDrug] = useState<Drug | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  // Session counter bumps on every open so the dialog remounts fresh each time
  // and useState re-initializes from the current drug prop (see SCHEMA.md §18 2026-07-20).
  const [sessionId, setSessionId] = useState(0);
  const openAdd = () => { setEditDrug(null); setSessionId((n) => n + 1); setAddOpen(true); };
  const openEdit = (d: Drug) => { setEditDrug(d); setSessionId((n) => n + 1); setAddOpen(true); };
  // Debounced (250ms): one /drugs?q= per typing pause, not per keystroke.
  const debouncedQ = useDebouncedValue(q, 250);
  const { data, isLoading } = useDrugs(debouncedQ.length >= 3 ? debouncedQ : "");
  const deleteMut = useDeleteDrug();
  const rows = Array.isArray(data) ? data : (data as any)?.rows ?? [];

  return (
    <div className="mx-auto max-w-7xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">Drug Master</h1>
        {canCreate && <Button className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={openAdd}><Plus size={16} className="mr-1" /> Add Drug</Button>}
      </div>

      <Tabs defaultValue="items">
        <TabsList>
          <TabsTrigger value="items">Items</TabsTrigger>
          <TabsTrigger value="bulk">Bulk import</TabsTrigger>
        </TabsList>
        <TabsContent value="items">
          <ItemsTab
            rows={rows}
            isLoading={isLoading}
            q={q}
            setQ={setQ}
            canEdit={canEdit}
            canDelete={canDelete}
            openEdit={openEdit}
            deleteMut={deleteMut}
          />
        </TabsContent>
        <TabsContent value="bulk"><BulkImportTab canImport={canImport} /></TabsContent>
      </Tabs>

      <DrugFormDialog key={sessionId} open={addOpen} onOpenChange={setAddOpen} drug={editDrug} />
    </div>
  );
}

function ItemsTab({ rows, isLoading, q, setQ, canEdit, canDelete, openEdit, deleteMut }: {
  rows: Drug[];
  isLoading: boolean;
  q: string;
  setQ: (v: string) => void;
  canEdit: boolean;
  canDelete: boolean;
  openEdit: (d: Drug) => void;
  deleteMut: ReturnType<typeof useDeleteDrug>;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const bulkDel = useBulkDeleteDrugs();

  const allSelected = rows.length > 0 && selected.size === rows.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));
  const toggleOne = (id: string) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const doBulkDelete = async () => {
    try {
      await bulkDel.mutateAsync([...selected]);
      setSelected(new Set());
      setConfirmOpen(false);
    } catch { /* handled */ }
  };

  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <Input placeholder="Search drugs (3+ chars)…" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {canDelete && selected.size > 0 && (
          <Button variant="destructive" onClick={() => setConfirmOpen(true)}>
            <Trash size={14} className="mr-1" /> Delete selected ({selected.size})
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-cream-100 text-maroon-700 dark:bg-dark-700 dark:text-amber-500">
                {canDelete && (
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allSelected ? true : selected.size > 0 ? "indeterminate" : false}
                      onCheckedChange={toggleAll}
                      aria-label="Select all drugs"
                    />
                  </TableHead>
                )}
                <TableHead>Name</TableHead><TableHead>Composition</TableHead><TableHead>Formulation</TableHead><TableHead>Strength</TableHead><TableHead className="w-20">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? <TableRow><TableCell colSpan={canDelete ? 6 : 5}><Skeleton className="h-10" /></TableCell></TableRow> :
               rows.length === 0 ? <TableRow><TableCell colSpan={canDelete ? 6 : 5} className="text-center text-sm text-muted py-8">No drugs</TableCell></TableRow> :
               rows.map((d: Drug) => (
                <TableRow key={d.id} className="even:bg-cream-50/50">
                  {canDelete && (
                    <TableCell>
                      <Checkbox
                        checked={selected.has(d.id)}
                        onCheckedChange={() => toggleOne(d.id)}
                        aria-label={`Select ${d.name}`}
                      />
                    </TableCell>
                  )}
                  <TableCell className="font-medium">{d.name}</TableCell>
                  <TableCell>{d.composition ?? "—"}</TableCell>
                  <TableCell>{d.formulation ?? "—"}</TableCell>
                  <TableCell>{d.strength ?? "—"}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {canEdit && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(d)}><Pencil size={14} /></Button>}
                      {canDelete && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => deleteMut.mutate(d.id)}><Trash size={14} /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Delete selected drugs?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted">
            {selected.size} drug{selected.size === 1 ? "" : "s"} will be permanently deleted. Prescriptions that
            reference them keep their copied name/strength text (the link is cleared automatically). This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={doBulkDelete} disabled={bulkDel.isPending}>
              {bulkDel.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

type PreviewRow = { name: string; composition?: string; formulation?: string; strength?: string };

function BulkImportTab({ canImport }: { canImport: boolean }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const bulk = useBulkImportDrugs();
  const [preview, setPreview] = useState<PreviewRow[] | null>(null);
  const formulations = useMemo(() => FORMULATIONS.map((f: any) => String(f.value ?? f)), []);

  const handleFile = async (file: File) => {
    if (!/\.xlsx$/i.test(file.name)) {
      toast.error("Only .xlsx files are supported. Download the template for the expected format.");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    try {
      // Dynamic import keeps the ~430KB xlsx parser out of the main bundle;
      // it is only loaded when a user actually picks a file.
      const XLSX = await import("xlsx");
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      if (!ws) throw new Error("The workbook has no sheets.");
      const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: null });
      const rows: PreviewRow[] = [];
      const badFormulations = new Set<string>();
      for (const r of raw) {
        const name = String(r.name ?? r.Name ?? "").trim();
        if (!name) continue; // skip blank names
        const composition = String(r.composition ?? r.Composition ?? "").trim();
        const formulationRaw = String(r.formulation ?? r.Formulation ?? "").trim();
        const strength = String(r.strength ?? r.Strength ?? "").trim();
        if (formulationRaw && !formulations.includes(formulationRaw)) badFormulations.add(formulationRaw);
        rows.push({
          name,
          composition: composition || undefined,
          formulation: (formulationRaw && formulations.includes(formulationRaw) ? formulationRaw : undefined),
          strength: strength || undefined,
        });
      }
      if (rows.length === 0) {
        toast.error("No rows found. The file must have a header row with a `name` column (composition, formulation, strength optional).");
        if (fileRef.current) fileRef.current.value = "";
        return;
      }
      if (badFormulations.size > 0) {
        toast.warning(`Ignored unknown formulation value(s): ${[...badFormulations].join(", ")} — allowed: ${formulations.join(", ")}`);
      }
      setPreview(rows);
    } catch (e) {
      toast.error(e instanceof Error && e.message ? `Could not read the file: ${e.message}` : "Could not read the file. Is it a valid .xlsx workbook?");
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const doImport = async () => {
    if (!preview) return;
    try {
      await bulk.mutateAsync(preview);
      setPreview(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch { /* handled */ }
  };

  const downloadTemplate = async () => {
    try {
      // window.open() can't attach the Appwrite JWT — fetch with auth and
      // hand the browser a blob URL instead.
      const blob = await apiFetchBlob("/drugs/template");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "drug-template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the template");
    }
  };

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-lg text-maroon-700 dark:text-amber-500">Bulk Import</h2>
          <Button variant="outline" onClick={downloadTemplate}><Download size={14} className="mr-1" /> Download template</Button>
        </div>
        <p className="text-sm text-muted">
          Upload an Excel (<code>.xlsx</code>) file with columns: <code>name</code> (required), <code>composition</code>,{" "}
          <code>formulation</code> (one of {formulations.join(" / ")}), <code>strength</code>. Existing drugs with
          the same name will be updated; new ones inserted.
        </p>
        {canImport && (
          <>
            <input ref={fileRef} type="file" accept=".xlsx" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} className="block" />
            {preview && (
              <div className="space-y-2">
                <p className="text-sm font-medium">{preview.length} rows ready to import</p>
                <div className="max-h-64 overflow-auto rounded border border-cream-200">
                  <Table>
                    <TableHeader><TableRow className="bg-cream-100"><TableHead>Name</TableHead><TableHead>Composition</TableHead><TableHead>Formulation</TableHead><TableHead>Strength</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {preview.slice(0, 50).map((r, i) => (
                        <TableRow key={i}><TableCell>{r.name}</TableCell><TableCell>{r.composition ?? "—"}</TableCell><TableCell>{r.formulation ?? "—"}</TableCell><TableCell>{r.strength ?? "—"}</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {preview.length > 50 && <p className="p-2 text-xs text-muted">… and {preview.length - 50} more</p>}
                </div>
                <Button onClick={doImport} disabled={bulk.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50"><Upload size={14} className="mr-1" /> Import {preview.length} rows</Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
